import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router'
import React from 'react'
import { useTitle } from '../hooks/use-title'
import { useTranslation } from 'react-i18next'
import { Badge, Button, Tabs, Tooltip } from 'antd'
import { ColleagueTable } from '../user/components/colleague-table'
import { z } from 'zod'
import { useAuth } from '../auth'
import { useCompanyUsers } from '../company/hooks'
import { CompanyUserInviteModal } from '../company/components/company-user-invite-modal'
import { INDEX_COMPANY_USER, INVITE_COMPANY_USER } from '../user/permission'
import { NotSubscribedResult } from '../components/not-subscribed-result'
import { useInvitations } from '../invitation/hooks'
import { InvitationsTable } from '../invitation/components/invitations-table'
import { MEMBER } from '../company/company-role'
import { PageTitle } from '../components/page-title'

export const Route = createFileRoute('/_auth/_main/colleagues')({
    validateSearch: z.object({
        invite: z.boolean().optional(),
    }),
    beforeLoad: ({ context }) => {
        if (!context.auth.can(INDEX_COMPANY_USER)) {
            throw redirect({
                to: '/dashboard',
            })
        }
    },
    component: Colleagues,
})

function Colleagues() {
    const { t } = useTranslation()

    useTitle(t('Colleagues'))
    const { activeCompany, can, hasSubscription } = useAuth()
    const { invite } = Route.useSearch()
    const navigate = useNavigate()

    const query = useCompanyUsers(activeCompany?.id)
    const invitationsQuery = useInvitations(activeCompany?.id, MEMBER)

    const hasAvailableSlots = query.isPending || (query.data && query.data?.length <= 5)

    return (
        <>
            <PageTitle
                title={t('Colleagues')}
                extra={
                    can(INVITE_COMPANY_USER) &&
                    hasSubscription && (
                        <Tooltip title={!hasAvailableSlots ? t('You can only add up to 5 colleagues') : undefined}>
                            <Link
                                to="."
                                search={{
                                    invite: true,
                                }}
                                className="w-full sm:w-auto"
                            >
                                <Button
                                    size="large"
                                    type="primary"
                                    disabled={!hasAvailableSlots}
                                    block
                                >
                                    {t('Invite colleague')}
                                </Button>
                            </Link>
                        </Tooltip>
                    )
                }
            />
            {hasSubscription ? (
                <Tabs
                    items={[
                        {
                            key: 'colleagues',
                            label: t('Colleagues'),
                            children: <ColleagueTable dataSource={query.data} />,
                        },
                        {
                            key: 'invitations',
                            label: (
                                <Badge
                                    count={invitationsQuery.data?.length}
                                    size="small"
                                    offset={[12, 0]}
                                >
                                    {t('Pending invitations')}
                                </Badge>
                            ),
                            children: (
                                <InvitationsTable
                                    companyId={activeCompany?.id}
                                    dataSource={invitationsQuery.data}
                                    loading={invitationsQuery.isPending}
                                />
                            ),
                        },
                    ]}
                />
            ) : (
                <NotSubscribedResult />
            )}
            <CompanyUserInviteModal
                companyId={activeCompany?.id}
                onCancel={() => {
                    navigate({
                        to: '.',
                        search: {},
                    })
                }}
                open={invite}
            />
        </>
    )
}
